import React from "react";
import { Link } from "react-router-dom";
import { Sun, Gauge, FileSearch, ArrowRight, CheckCircle2, Zap } from "lucide-react";
import WattZapLogo from "../components/WattZapLogo";
import { usePrefersReducedMotion, useInView } from "../components/journey/hooks";
import LeaseOwnChart from "../components/journey/LeaseOwnChart";
import { buildWattzapOSUrl, openEnergyScanPortal } from "../lib/wattzapOS";
import ChargeHubPreviewSection from "../chargehub/ChargeHubPreviewSection";
import { useT } from "../i18n/LanguageContext";

function HomeJourneyChart() {
  const t = useT();
  const reduced = usePrefersReducedMotion();
  const [ref, inView] = useInView(0.25);
  return (
    <div ref={ref} className="mt-12">
      <Link
        to="/journey"
        data-testid="home-leaseown-chart"
        className="block bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#00D4FF]/40 transition-colors group"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="font-heading text-lg text-white">{t("Lease it, then own it — one mill's 25-year map")}</div>
          <span className="inline-flex items-center gap-2 text-[#00D4FF] text-sm whitespace-nowrap group-hover:gap-3 transition-all">
            {t("The Savings Journey")} <ArrowRight size={16} />
          </span>
        </div>
        <LeaseOwnChart show={inView || reduced} reduced={reduced} compact />
      </Link>
    </div>
  );
}

function Pillar({ icon: Icon, title, what, outcome, testId, to }) {
  const t = useT();
  return (
    <Link to={to} data-testid={testId} className="block bg-white border border-slate-100 rounded-2xl p-7 shadow-card-soft hover:-translate-y-1 hover:shadow-cyan-glow/30 transition-all">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF] mb-5">
        <Icon size={22} />
      </div>
      <h3 className="text-xl font-heading text-graphite mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{t(what)}</p>
      <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-graphite font-medium flex items-start gap-2">
        <CheckCircle2 size={16} className="text-[#21F59B] mt-0.5 shrink-0" />
        <span>{t(outcome)}</span>
      </div>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#00D4FF]">{t("Explore")} <ArrowRight size={14} /></span>
    </Link>
  );
}

function Step({ n, title, body }) {
  const t = useT();
  return (
    <div className="relative flex-1 min-w-[220px]">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-wz-gradient text-[#071A2D] font-heading flex items-center justify-center text-sm">{n}</div>
        <h4 className="font-heading text-lg text-graphite">{t(title)}</h4>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{t(body)}</p>
    </div>
  );
}

function StatTile({ value, label, testId }) {
  const t = useT();
  return (
    <div data-testid={testId} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 backdrop-blur">
      <div className="text-4xl font-heading text-white">
        {t(value)}
      </div>
      <div className="text-sm text-white/60 mt-2">{t(label)}</div>
      <div className="mt-4 h-1 w-10 bg-gold rounded-full" />
    </div>
  );
}

export default function Home() {
  const t = useT();
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[#071A2D] text-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="mb-10">
            <WattZapLogo variant="full" entrance={true} height={76} dark={true} />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            {t("Smart energy.")} <span className="text-gradient-brand">{t("Lower bills.")}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
            {t("WattZap combines rooftop solar, intelligent energy management, and bill analytics to cut power costs for commercial and industrial businesses — with zero upfront investment.")}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="hero-cta-primary" className="btn-gradient inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
              {t("Get a free EnergyScan")} <ArrowRight size={16} />
            </a>
            <Link to="/products" data-testid="hero-cta-secondary" className="btn-outline-brand inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
              {t("Explore our products")}
            </Link>
          </div>

          {/* micro-badges */}
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs text-white/60">
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF]" /> {t("Zero capex")}</span>
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#21F59B]" /> {t("Per-unit savings from day one")}</span>
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#FFC857]" /> {t("Serving industries across India · Deep DISCOM expertise in Telangana & AP")}</span>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#00D4FF] mb-3">{t("What we do")}</div>
            <h2 className="font-heading text-3xl lg:text-4xl text-graphite">{t("Three services, one lower bill.")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{t("Every WattZap engagement combines assets, software and analytics — so you save on generation, consumption and tariffs at the same time.")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Pillar testId="pillar-solar" icon={Sun} title="SolarLease" to="/products/solarlease"
                    what="We design, install, own and operate a rooftop solar plant on your facility."
                    outcome="You pay less per unit than the grid — with zero upfront investment." />
            <Pillar testId="pillar-ems" icon={Gauge} title="EMS Lite" to="/products/ems-lite"
                    what="Live monitoring, load scheduling, peak-demand alerts and power-factor tracking."
                    outcome="See exactly where every rupee of your bill goes — and cut the waste." />
            <Pillar testId="pillar-billscan" icon={FileSearch} title="EnergyScan" to="/products/energyscan"
                    what="We audit your last 12 months of bills against local tariff structures."
                    outcome="Find savings in demand charges, PF penalties and tariff categorisation." />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-mist py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#00D4FF] mb-3">{t("How it works")}</div>
            <h2 className="font-heading text-3xl lg:text-4xl text-graphite">{t("From bill upload to live savings in weeks.")}</h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-6 items-stretch">
            <Step n="1" title="Share your electricity bills" body="Send us the last 12 months of bills. That's all we need to start." />
            <div className="hidden lg:flex items-center px-2"><Zap className="text-[#00D4FF]" size={22} /></div>
            <Step n="2" title="We audit tariff, loads & solar potential" body="Our engineers analyse your consumption profile and site." />
            <div className="hidden lg:flex items-center px-2"><Zap className="text-[#21F59B]" size={22} /></div>
            <Step n="3" title="We install solar + EMS at zero capex" body="We fund, own and operate the assets. You approve the design." />
            <div className="hidden lg:flex items-center px-2"><Zap className="text-[#FFC857]" size={22} /></div>
            <Step n="4" title="Pay less per unit — see savings live" body="Cheaper power from day one, with a real-time dashboard." />
          </div>
        </div>
      </section>

      {/* THE NEXT WATTZAP UNLOCK — ChargeHub (additive section) */}
      <ChargeHubPreviewSection />

      {/* WHY WATTZAP */}
      <section className="bg-[#071A2D] text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#FFC857] mb-3">{t("Why WattZap")}</div>
            <h2 className="font-heading text-3xl lg:text-4xl">{t("Built for industrial reality, not slideware.")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <StatTile testId="stat-capex" value="₹0" label="Upfront investment" />
            <StatTile testId="stat-freescan" value="Free" label="EnergyScan diagnosis" />
            <StatTile testId="stat-leaseown" value="25 yr" label="Lease-then-own solar journey" />
            <StatTile testId="stat-verified" value="Every ₹" label="Verified on your electricity bill" />
          </div>
          <HomeJourneyChart />
          <div className="mt-12">
            <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="why-cta" className="btn-gradient inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm">
              {t("Get a free EnergyScan")} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
